import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'components/Button';
import { Grid } from 'components/Grid';
import { Image } from 'components/Image';
import { Text } from 'components/Text';
import { VideoAutoplay } from 'components/VideoAutoplay';
import { Testimonial } from 'patterns/Testimonial';
import { cx, capitalizeFirstLetter } from 'utility';
import * as styles from './FeaturedCaseStudy.module.scss';

const propTypes = {
    alignment: PropTypes.string,
    avatar: PropTypes.PropTypes.shape({
        alt: PropTypes.string,
        dimensions: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
        }),
        url: PropTypes.string,
    }),
    bio: PropTypes.string,
    buttonText: PropTypes.string,
    buttonUrl: PropTypes.object,
    className: PropTypes.string,
    image: PropTypes.PropTypes.shape({
        alt: PropTypes.string,
        dimensions: PropTypes.shape({
            height: PropTypes.number,
            width: PropTypes.number,
        }),
        url: PropTypes.string,
    }),
    name: PropTypes.string,
    quote: PropTypes.string,
    stats: PropTypes.arrayOf(PropTypes.object),
};

const defaultProps = {
    alignment: '',
    avatar: {},
    bio: '',
    buttonText: '',
    buttonUrl: {},
    className: '',
    image: {},
    name: '',
    quote: '',
    stats: [],
};

function getClasses({ className, alignment }) {
    const classes = cx(
        className,
        styles['featuredCaseStudy'],
        styles[`featuredCaseStudy${capitalizeFirstLetter(alignment)}`],
    );

    return classes || undefined;
}

function FeaturedCaseStudy({
    alignment,
    avatar,
    bio,
    buttonText,
    buttonUrl,
    className,
    image,
    name,
    quote,
    stats,
    videoUploadUrl,
}) {
    const classes = getClasses({ className, alignment });

    return (
        <div className={classes}>
            <Grid>
                <div className={styles['container']}>
                    <div className={styles['videoContainer']}>
                        <div className={styles['videoWrapper']}>
                            <div className={styles['videoInner']}>
                                <Image
                                    className={styles['image']}
                                    image={image}
                                    size="xsmall"
                                />
                                {videoUploadUrl && (
                                    <VideoAutoplay
                                        className={styles['video']}
                                        loop={false}
                                        videoUploadUrl={videoUploadUrl}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles['contentContainer']}>
                        {(quote && name) && (
                            <Testimonial
                                background={true}
                                bio={bio}
                                className={styles['testimonial']}
                                gridClassName={styles['testimonialGrid']}
                                image={avatar}
                                name={name}
                                quote={quote}
                                size="medium"
                            />
                        )}
                        <ul className={styles['statList']}>
                            {stats.map(({ stat_label, stat_text }, index) => {
                                const statLabel = stat_label?.text;
                                const statText = stat_text?.text;

                                return (
                                    <li className={styles['statItem']} key={`featured-case-study-${index}`}>
                                        <Text as="span" className={styles['statText']} emphasis="medium" size={7}>{statText}</Text>
                                        <Text as="span" className={styles['statLabel']} size={3}>{statLabel}</Text>
                                    </li>
                                );
                            })}
                        </ul>
                        {(buttonText && (buttonUrl?.url || buttonUrl?.uid)) && (
                            <Button className={styles['button']} color="secondary" to={buttonUrl}>{buttonText}</Button>
                        )}
                    </div>
                </div>
            </Grid>
        </div>
    );
}

FeaturedCaseStudy.propTypes = propTypes;
FeaturedCaseStudy.defaultProps = defaultProps;

export { FeaturedCaseStudy };
